import { FileText, CheckCircle, AlertTriangle, Scale, Users, Shield } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-gray-50 py-6 md:py-12 pb-20 md:pb-12">
      <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl md:rounded-2xl p-5 md:p-8 mb-6 md:mb-8 text-white">
          <div className="flex items-center mb-3 md:mb-4">
            <Scale className="h-8 w-8 md:h-12 md:w-12 mr-3 md:mr-4" />
            <div>
              <h1 className="text-2xl md:text-4xl font-bold">Terms & Conditions</h1>
              <p className="text-primary-100 text-xs md:text-base mt-1 md:mt-2">Last updated: October 16, 2025</p>
            </div>
          </div>
          <p className="text-sm md:text-lg text-primary-50">
            Please read these terms and conditions carefully before using RAPSAPP services.
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-8 space-y-6 md:space-y-8">
          
          {/* Section 1 */}
          <section>
            <div className="flex items-start mb-4">
              <FileText className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">1. Acceptance of Terms</h2>
                <div className="text-gray-700 space-y-3">
                  <p>By accessing and using RAPSAPP ("the Platform"), you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.</p>
                  <p>These terms apply to all users of the Platform, including customers and service professionals.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <Users className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">2. User Accounts</h2>
                <div className="text-gray-700 space-y-3">
                  <p><strong>Account Registration:</strong></p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You must be at least 18 years old to create an account</li>
                    <li>You must provide accurate and complete information</li>
                    <li>You are responsible for maintaining account confidentiality</li>
                    <li>You are responsible for all activities under your account</li>
                    <li>You must notify us immediately of any unauthorized use</li>
                  </ul>
                  
                  <p className="mt-4"><strong>Account Termination:</strong></p>
                  <p>We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent activities.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <CheckCircle className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">3. Services Provided</h2>
                <div className="text-gray-700 space-y-3">
                  <p>RAPSAPP is a platform that connects customers with verified service professionals. We provide:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>A marketplace to discover and book home services</li>
                    <li>Verified professional profiles and ratings</li>
                    <li>Booking and payment processing</li>
                    <li>Customer support and dispute resolution</li>
                  </ul>
                  
                  <p className="mt-4"><strong>Important:</strong> RAPSAPP acts as an intermediary platform. We do not directly provide services but facilitate connections between customers and service professionals.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <Shield className="h-6 w-6 text-primary-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">4. Booking and Payments</h2>
                <div className="text-gray-700 space-y-3">
                  <p><strong>Booking Process:</strong></p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Service bookings are subject to professional availability</li>
                    <li>Prices are displayed clearly before booking</li>
                    <li>Booking confirmation will be sent via email/SMS</li>
                    <li>You may reschedule or cancel as per our cancellation policy</li>
                  </ul>
                  
                  <p className="mt-4"><strong>Payment Terms:</strong></p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All prices are in Indian Rupees (INR)</li>
                    <li>Payment is processed securely through authorized gateways</li>
                    <li>Payment confirmation is required before service delivery</li>
                    <li>Service charges and taxes are clearly mentioned</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">5. Cancellation and Refund Policy</h2>
            <div className="text-gray-700 space-y-3">
              <p><strong>Cancellation by Customer:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>24+ hours before service:</strong> Full refund</li>
                <li><strong>12-24 hours before service:</strong> 50% refund</li>
                <li><strong>Less than 12 hours:</strong> No refund</li>
                <li><strong>Emergency situations:</strong> Considered on case-by-case basis</li>
              </ul>
              
              <p className="mt-4"><strong>Cancellation by Professional:</strong></p>
              <p>If a service professional cancels, you will receive a full refund or be offered an alternative professional at no extra cost.</p>
              
              <p className="mt-4">Refunds are processed within 5-7 business days to the original payment method.</p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">6. User Responsibilities</h2>
            <div className="text-gray-700 space-y-3">
              <p>As a user of RAPSAPP, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate information for service delivery</li>
                <li>Be present at the scheduled time for service</li>
                <li>Treat service professionals with respect</li>
                <li>Provide a safe working environment</li>
                <li>Report any issues or concerns promptly</li>
                <li>Not misuse the platform for illegal activities</li>
                <li>Not share false reviews or ratings</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section className="border-t pt-8">
            <div className="flex items-start mb-4">
              <AlertTriangle className="h-6 w-6 text-amber-600 mr-3 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">7. Liability and Disclaimers</h2>
                <div className="text-gray-700 space-y-3">
                  <p><strong>Service Quality:</strong></p>
                  <p>While we verify service professionals, RAPSAPP is not liable for the quality of services provided. However, we offer a satisfaction guarantee and will work to resolve any issues.</p>
                  
                  <p className="mt-4"><strong>Limitation of Liability:</strong></p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>RAPSAPP is not liable for indirect or consequential damages</li>
                    <li>Our liability is limited to the amount paid for the service</li>
                    <li>We are not responsible for delays due to circumstances beyond our control</li>
                  </ul>
                  
                  <p className="mt-4"><strong>Property Damage:</strong></p>
                  <p>Service professionals carry insurance for accidental damage. Report any damage immediately for claims processing.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">8. Intellectual Property</h2>
            <div className="text-gray-700 space-y-3">
              <p>All content on RAPSAPP, including text, graphics, logos, and software, is owned by RAPSAPP or its licensors. You may not:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Copy or reproduce platform content without permission</li>
                <li>Use our trademarks or branding</li>
                <li>Modify or create derivative works</li>
                <li>Use automated systems to access the platform</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">9. Privacy</h2>
            <div className="text-gray-700 space-y-3">
              <p>Your use of RAPSAPP is also governed by our Privacy Policy. Please review our Privacy Policy to understand how we collect and use your information.</p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">10. Dispute Resolution</h2>
            <div className="text-gray-700 space-y-3">
              <p><strong>Customer Support:</strong></p>
              <p>For any disputes or issues, please contact our customer support first. We are committed to resolving issues amicably.</p>
              
              <p className="mt-4"><strong>Governing Law:</strong></p>
              <p>These terms are governed by the laws of India. Any disputes will be subject to the jurisdiction of courts in Himachal Pradesh, India.</p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="border-t pt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">11. Changes to Terms</h2>
            <div className="text-gray-700 space-y-3">
              <p>We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of the platform constitutes acceptance of modified terms.</p>
            </div>
          </section>

          {/* Contact */}
          <section className="border-t pt-8 bg-gray-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Contact Us</h2>
            <div className="text-gray-700 space-y-2">
              <p>If you have questions about these Terms & Conditions, please contact us:</p>
              <div className="space-y-1 mt-4">
                <p><strong>Email:</strong> <a href="mailto:rapsappservice@gmail.com" className="text-primary-600 hover:underline">rapsappservice@gmail.com</a></p>
                <p><strong>Phone:</strong> <a href="tel:+917018401540" className="text-primary-600 hover:underline">+91 7018401540</a></p>
                <p><strong>Address:</strong> Himachal Pradesh, India</p>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
